import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type {
  DynamicSubRoute,
  DynamicSubRouteProps,
} from '@ipen-portal/plugin-common-react';
import type { JSX } from 'react';

import { isEqual } from 'es-toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type DynamicRoute = Record<
  string,
  {
    dynamicGroups?: EntityContentGroupDefinitions;
    loading: boolean;
    routes: DynamicSubRoute[];
  }
>;

export const useResolveDynamicRoutes = (
  dynamicRouteDefinitions: {
    Component: (props: DynamicSubRouteProps) => JSX.Element;
    id: string;
  }[],
) => {
  const [dynamicRoutes, setDynamicRoutes] = useState<DynamicRoute>({});

  // Clean up dynamicRoutes when dynamicRouteDefinitions change
  useEffect(() => {
    const knownIds = new Set(dynamicRouteDefinitions.map(def => def.id));
    setDynamicRoutes(prevRoutes => {
      const newRoutes = { ...prevRoutes };
      // Remove entries that are no longer in dynamicRouteDefinitions
      let removed = false;
      for (const id of Object.keys(newRoutes)) {
        if (!knownIds.has(id)) {
          removed = true;
          delete newRoutes[id];
        }
      }
      return removed ? newRoutes : prevRoutes;
    });
  }, [dynamicRouteDefinitions]);

  const updateSubRoutes: DynamicSubRouteProps['resolveSubRoutes'] = useCallback(
    (data, dynamicId) => {
      setDynamicRoutes(prevState => {
        const newsState = {
          dynamicGroups:
            'dynamicGroups' in data ? data.dynamicGroups : undefined,
          loading: 'loading' in data && data.loading,
          routes: 'routes' in data ? data.routes : [],
        };
        if (
          dynamicId in prevState
          && isEqual(prevState[dynamicId], newsState)
        ) {
          return prevState;
        }
        return {
          ...prevState,
          [dynamicId]: newsState,
        };
      });
    },
    [setDynamicRoutes],
  );

  return useMemo(
    () => ({
      dynamicRoutes,
      dynamicRoutesElement: dynamicRouteDefinitions.map(def => (
        <def.Component key={def.id} resolveSubRoutes={updateSubRoutes} />
      )),
    }),
    [dynamicRouteDefinitions, dynamicRoutes, updateSubRoutes],
  );
};
